import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Flame, ShieldCheck, Award } from "lucide-react";

const ACCENT = "#0062A8"; // Ford blue
const ACCENT_HOT = "#E8372C";

export default function FordHighlight() {
  return (
    <section
      className="relative py-24 sm:py-32 border-t border-white/5 overflow-hidden"
      data-testid="ford-highlight-section"
      aria-label="Ford EcoBoost motorfelújítás specialitásunk"
    >
      {/* Background glow */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute top-10 left-1/4 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.09]"
          style={{ background: `radial-gradient(circle, ${ACCENT} 0%, transparent 70%)` }}
        />
        <div
          className="absolute bottom-0 right-10 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-[0.06]"
          style={{ background: `radial-gradient(circle, ${ACCENT_HOT} 0%, transparent 70%)` }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Eyebrow row */}
        <div className="flex items-center gap-3 mb-6">
          <span
            className="w-2.5 h-2.5 rounded-full animate-pulse"
            style={{ background: ACCENT_HOT }}
            aria-hidden="true"
          />
          <span
            className="text-[0.65rem] sm:text-[0.72rem] font-bold uppercase tracking-[0.2em] sm:tracking-[0.3em]"
            style={{ color: ACCENT_HOT, fontFamily: "Barlow Condensed" }}
          >
            Specialitásunk · Ford EcoBoost
          </span>
        </div>

        {/* Headline */}
        <h2
          className="font-black uppercase tracking-tight text-white leading-[0.92] max-w-4xl"
          style={{
            fontFamily: "Barlow Condensed",
            fontSize: "clamp(2rem, 6vw, 5rem)",
          }}
          data-testid="ford-highlight-headline"
        >
          Ha a Ford márkaszerviz
          <br />
          <span style={{ color: ACCENT }}>új motort javasolt</span> — beszéljünk előbb.
        </h2>

        <p className="mt-6 max-w-2xl text-zinc-300 text-base sm:text-lg leading-relaxed">
          Ford EcoBoost motorok teljes körű felújítása blokkperselyezéssel:
          1.0, 1.5, 1.6, 2.0 és 2.3 EcoBoost — gyárinál jobb technológiával,
          írásbeli garanciával. Debrecen egyik egyedülálló szolgáltatása.
        </p>

        {/* Main card: image + content */}
        <div
          className="mt-12 relative bg-[#0d0d0d] border border-white/10 overflow-hidden shadow-2xl"
          data-testid="ford-highlight-card"
        >
          <div className="m-stripe absolute top-0 left-0 right-0 h-[2px] z-10" />

          <div className="grid lg:grid-cols-12 items-stretch">
            {/* IMAGE */}
            <div className="relative lg:col-span-7 bg-black">
              <img
                src="/images/ford-ecoboost-perselyezett-blokk.webp"
                alt="Felújított Ford EcoBoost rövidblokk — perselyezett hengerek, STD dugattyúk"
                className="w-full h-full object-cover"
                loading="lazy"
                width="2000"
                height="834"
                data-testid="ford-highlight-image"
              />

              {/* Top-left label */}
              <div className="absolute top-5 left-5 flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-white/10 px-3 py-1.5 pointer-events-none">
                <span
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ background: ACCENT }}
                  aria-hidden="true"
                />
                <span
                  className="text-[0.6rem] sm:text-[0.65rem] font-bold uppercase tracking-[0.18em] sm:tracking-[0.2em] text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Elkészült EcoBoost blokk · 4 persely
                </span>
              </div>

              {/* Bottom-right watermark */}
              <div className="absolute bottom-5 right-5 bg-black/70 backdrop-blur-sm border border-white/10 px-3 py-1.5 pointer-events-none hidden sm:block">
                <span
                  className="text-[0.6rem] font-bold uppercase tracking-[0.22em] text-zinc-400"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  autoszerelem.hu · Debrecen
                </span>
              </div>
            </div>

            {/* CONTENT */}
            <div className="lg:col-span-5 p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
              <div
                className="font-black uppercase tracking-tight text-white leading-none mb-6"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(1.5rem, 2.6vw, 2.5rem)",
                }}
                data-testid="ford-highlight-engines"
              >
                1.0 · 1.5 · 1.6 · 2.0 · 2.3
                <br />
                <span style={{ color: ACCENT }} className="text-sm font-bold tracking-[0.25em] mt-2 inline-block">
                  EcoBoost
                </span>
              </div>

              <ul className="space-y-4" data-testid="ford-highlight-bullets">
                {[
                  { Icon: ShieldCheck, text: "Blokkperselyezés gyárinál jobb technológiával" },
                  { Icon: Award, text: "Írásbeli garancia a kivitelezésre és anyagra" },
                  { Icon: Flame, text: "Hengerfal-repedés, hűtővíz-szivárgás javítása" },
                ].map(({ Icon, text }, i) => (
                  <li key={i} className="flex items-start gap-3 text-zinc-200 leading-relaxed">
                    <div
                      className="shrink-0 w-7 h-7 flex items-center justify-center mt-0.5"
                      style={{ background: `${ACCENT}18`, border: `1px solid ${ACCENT}40` }}
                      aria-hidden="true"
                    >
                      <Icon className="w-3.5 h-3.5" style={{ color: ACCENT }} strokeWidth={2} />
                    </div>
                    <span className="text-sm sm:text-base">{text}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/ford-ecoboost-felujitas"
                  className="btn-primary inline-flex items-center"
                  data-testid="ford-highlight-cta"
                >
                  Tovább az EcoBoost oldalra
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <a
                  href="tel:+36308637476"
                  className="btn-ghost inline-flex items-center"
                  data-testid="ford-highlight-call"
                >
                  <Phone className="w-4 h-4 mr-2" strokeWidth={2} />
                  Hívás
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
